import { useEffect, useRef, useState, type RefObject } from "react";
import { useOutletContext } from "react-router";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { ShellCloseButton } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar";
import { TitlePill } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import { Card, Section } from "@acto/taskpane/field-sales-2/components/primitives/Card";

// Direct flex child of the overlay region; carries the page-level card padding itself so no
// intermediate layout (with flexbox's min-height:auto floor) sits in the height chain.
export const OverlayFrame = styled("div")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

const FOOTER_WASH_PX = 154;

const footerClearance = (spacing: Record<number, string>) => `calc(${spacing[24]} + ${spacing[50]})`;

// minHeight:0 at every level of the chain, or the rows region refuses to shrink below its content
// and the scrollport never engages.
export const OverlayCard = styled(Card)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  position: "relative",
  background: theme.fieldSalesTwo.colors.surface.glass,
  borderRadius: theme.fieldSalesTwo.radius["44"],
  "&::after": {
    content: '""',
    position: "absolute",
    insetInline: 0,
    bottom: 0,
    height: FOOTER_WASH_PX,
    background: theme.fieldSalesTwo.colors.gradient.cardFooter,
    borderEndStartRadius: theme.fieldSalesTwo.radius["44"],
    borderEndEndRadius: theme.fieldSalesTwo.radius["44"],
    pointerEvents: "none",
    transition: `opacity ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  },
  '&[data-at-end="true"]::after': { opacity: 0 },
}));

export const OverlayBody = styled(Section)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  gap: 0,
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
}));

export const OverlayFooter = styled("div")(({ theme }) => ({
  position: "absolute",
  insetInline: 0,
  bottom: 0,
  zIndex: 1,
  padding: `0 ${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

export const ScrollArea = styled("div")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "clip",
  overscrollBehavior: "contain",
  // The track is laid out between the scrollport's padding box and its border box. Bleeding
  // spacing[6] past the body inset leaves the content box (and with it the list card) exactly where
  // the inset alone put it, with the track filling the card's remaining edge. scrollbarGutter holds
  // that reservation on a level that fits, so a short one does not resize the list.
  marginRight: `-${theme.fieldSalesTwo.spacing[6]}`,
  scrollbarGutter: "stable",
  "&::-webkit-scrollbar": { width: theme.fieldSalesTwo.spacing[6] },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: theme.fieldSalesTwo.colors.neutral[400],
    borderRadius: theme.fieldSalesTwo.radius.circular,
  },
  // Declaring scrollbar-width or scrollbar-color in Blink or WebKit makes them ignore the
  // pseudo-element rules above, so the standard-property fallback is scoped to engines without.
  "@supports not selector(::-webkit-scrollbar)": {
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.fieldSalesTwo.colors.neutral[400]} transparent`,
  },
  paddingBottom: footerClearance(theme.fieldSalesTwo.spacing),
  "& > header": {
    gap: theme.fieldSalesTwo.spacing[4],
    paddingInline: theme.fieldSalesTwo.spacing[16],
    paddingBottom: theme.fieldSalesTwo.spacing[24],
  },
  '& > header[data-has-headline="false"]': { paddingBottom: theme.fieldSalesTwo.spacing[16] },
  '& > header[data-has-subheader="true"]': { paddingBottom: theme.fieldSalesTwo.spacing[4] },
}));

export const Sentinel = styled("div")({ width: "100%", height: 1 });

// The negative margin lays the probe over the scrollport's bottom padding, so it is fully visible
// only once the scroll has reached its end.
export const ScrollEndProbe = styled("div")(({ theme }) => ({
  height: footerClearance(theme.fieldSalesTwo.spacing),
  marginBottom: `calc(-1 * ${footerClearance(theme.fieldSalesTwo.spacing)})`,
  pointerEvents: "none",
}));

export function useScrolledToEnd(scrollRef: RefObject<HTMLDivElement | null>) {
  const probeRef = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const root = scrollRef.current;
    const probe = probeRef.current;
    if (!root || !probe) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setAtEnd(entry.intersectionRatio >= 0.99);
      },
      { root, threshold: [0, 0.99] },
    );
    observer.observe(probe);
    return () => observer.disconnect();
  }, [scrollRef]);

  return { probeRef, atEnd };
}

/** Claims the shell's top-bar leading area for an open drill: the X plus the guide pill. */
export function DrillGuideTopBar({ id }: { id: string }) {
  const t = useTranslations("fieldSalesTwo.drill");
  const customer = useOutletContext<CustomerOutletContext | null>()?.customer;
  const guideOrigin = customer?.name || customer?.externalReference;

  return (
    <ShellSlotContent slot="topBarLeading" id={id}>
      <ShellCloseButton />
      <TitlePill subtitle={guideOrigin} subtitleTone="neutral">
        {t("guide")}
      </TitlePill>
    </ShellSlotContent>
  );
}
